/**
 * Turns API failures into plain-language errors.
 *
 * The raw server text ("Request failed (422)", "Internal Server Error", a
 * database constraint name) must never reach a user. Everything the API
 * clients throw is an ApiError whose `message` is already safe to show, plus
 * `fieldErrors` so a form can point at the field that is wrong.
 */

export const OFFLINE_MESSAGE = "Couldn't reach the server. Check your connection and try again.";

export type FieldErrors = Record<string, string>;

const GENERIC: Record<number, string> = {
  400: 'Something in what you entered needs another look. Please check it and try again.',
  401: 'Your session has ended. Please sign in again.',
  403: "You don't have permission to do that.",
  404: "We couldn't find that. It may have been deleted or moved.",
  408: 'The request took too long. Please try again.',
  409: 'This record changed or is in a state that does not allow that. Reload and try again.',
  413: 'That file or request is too large.',
  422: 'Some details need fixing. Please check the highlighted fields and try again.',
  429: 'Too many requests in a short time. Please wait a moment and try again.',
};

const SERVER_SIDE = 'Something went wrong on our side. Please try again in a moment.';
const FALLBACK_CLIENT = 'That did not go through. Please check what you entered and try again.';

/** A server message is only shown when it reads like a sentence a person wrote. */
function looksHuman(text: string | null | undefined): text is string {
  if (!text) return false;
  const t = text.trim();
  if (t.length < 4 || t.length > 200) return false;
  if (/[{}<>]|\n/.test(t)) return false;
  if (/\b(error:|exception|stack|undefined|null|violates|constraint|syntax|ECONN|ENOTFOUND|SQL|sequelize|knex|pg_)/i.test(t)) return false;
  if (/\bat \S+ \(|\.(js|ts):\d+/.test(t)) return false;
  if (/^(internal server error|bad request|unprocessable entity|not found|forbidden|unauthorized|conflict|request failed|api )/i.test(t)) return false;
  if (/\(\d{3}\)|\b[45]\d{2}\b/.test(t)) return false;
  return true;
}

/** Plain-language message for an HTTP status, using the server text only if it is human. */
export function friendlyErrorMessage(status: number, serverMessage?: string | null): string {
  if (status >= 500) return SERVER_SIDE;
  if (status === 401 || status === 403 || status === 404 || status === 429) return GENERIC[status];
  if (status >= 400 && looksHuman(serverMessage)) return serverMessage.trim();
  return GENERIC[status] ?? (status >= 400 ? FALLBACK_CLIENT : SERVER_SIDE);
}

function firstString(v: unknown): string | null {
  if (typeof v === 'string' && v.trim()) return v.trim();
  if (Array.isArray(v)) {
    for (const item of v) {
      const s = firstString(item);
      if (s) return s;
    }
  }
  return null;
}

/** Reads field-level validation errors out of the common API body shapes. */
export function fieldErrorsFrom(body: unknown): FieldErrors {
  if (!body || typeof body !== 'object') return {};
  const b = body as Record<string, unknown>;
  const raw = b.errors ?? b.fieldErrors ?? b.fields ?? b.details;
  const out: FieldErrors = {};
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const i = item as Record<string, unknown>;
      const field = firstString(i.field ?? i.path ?? i.param ?? i.name);
      const msg = firstString(i.message ?? i.msg ?? i.error);
      if (field && msg) out[field] = msg;
    }
  } else if (raw && typeof raw === 'object') {
    for (const [field, v] of Object.entries(raw as Record<string, unknown>)) {
      const msg = firstString(v);
      if (msg) out[field] = msg;
    }
  }
  return out;
}

export class ApiError extends Error {
  status: number | null;
  fieldErrors: FieldErrors;
  offline: boolean;
  /** Machine-readable `code` from the error body (e.g. `provider_not_configured`), when the API sent one. */
  code: string | null;
  /** The parsed error body, for the rare caller that needs more than the message (e.g. a 502 that carries the failed record). */
  body: unknown;

  constructor(
    message: string,
    opts: { status?: number | null; fieldErrors?: FieldErrors; offline?: boolean; code?: string | null; body?: unknown } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = opts.status ?? null;
    this.fieldErrors = opts.fieldErrors ?? {};
    this.offline = opts.offline ?? false;
    this.code = opts.code ?? null;
    this.body = opts.body ?? null;
  }

  static offline(): ApiError {
    return new ApiError(OFFLINE_MESSAGE, { offline: true });
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Builds the error for a failed response. `body` is the parsed JSON, if any. */
export function buildApiError(status: number, body: unknown): ApiError {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const fieldErrors = fieldErrorsFrom(body);
  const serverMessage = firstString(b.error) ?? firstString(b.message);
  const hasFields = Object.keys(fieldErrors).length > 0;
  // With field errors the page-level text just points at them; the specific
  // wording lives on each field.
  const message = hasFields && status < 500
    ? GENERIC[422]
    : friendlyErrorMessage(status, serverMessage);
  const code = typeof b.code === 'string' && b.code ? b.code : null;
  return new ApiError(message, { status, fieldErrors, code, body: body ?? null });
}

/** Message to show for anything caught: ApiError text is already plain. */
export function errorMessageOf(err: unknown, fallback: string): string {
  if (isApiError(err)) return err.message;
  if (err instanceof Error && err.message && looksHuman(err.message)) return err.message;
  return fallback;
}

/** A client-side check that failed, shaped like a 422 so a form shows each message under its own field. */
export function validationError(fieldErrors: FieldErrors): ApiError {
  return new ApiError('Please check the highlighted fields.', { status: 422, fieldErrors });
}
